import Carousel from "react-slick";
import { CSSProperties, useRef, useState } from "react";
import { Box, Stack, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/system";
import { Image } from "../Image";
import { CarouselArrows } from "./CarouselArrows";
import LightboxModal from "components/LightboxModal";
import { useModal } from "hooks/useModal";
import { preventBubbling } from "utils/listings/helper";
import { ImageNotSupported } from "@material-ui/icons";
import type { CropToImageProps } from "../Image";
import type { Listing } from "../../@types/listings/listing";

export type BlurredImageType = "weak" | "strong";

type CarouselCardProps = Pick<Listing, "pictures"> &
  CropToImageProps & {
    width?: number;
    height?: number;
    listingMatch?: boolean;
    imagesHidden?: boolean;
    arrowIconSize?: "small" | "medium" | "large";
    hiddenPictures?: string[];
    filterHiddenPictures?: boolean;
  };

export function CarouselCard({
  width,
  height,
  pictures,
  cropTopImage,
  imagesHidden,
  listingMatch,
  arrowIconSize,
  hiddenPictures,
  filterHiddenPictures
}: CarouselCardProps): JSX.Element {
  const [modalIndex, setModalIndex] = useState(0);

  const theme = useTheme();

  const carouselRef = useRef<Carousel | null>(null);

  const { open, openModal, closeModal } = useModal();

  const handlePrev = (): void => carouselRef.current?.slickPrev();

  const handleNext = (): void => carouselRef.current?.slickNext();

  const handleOpenModal = (index: number) => (): void => {
    setModalIndex(index);
    openModal();
  };

  const filteredPictures = getFilteredPictures(pictures, filterHiddenPictures, hiddenPictures);

  const images = filteredPictures?.map(({ src }) => src);

  const showPictures = filteredPictures?.length && !imagesHidden;

  const isRTL = theme.direction === "rtl";

  const carouselSettings = {
    dots: false,
    arrows: false,
    slidesToShow: 1,
    currentSlide: 0,
    slidesToScroll: 1,
    rtl: isRTL
  };

  if (!showPictures) {
    return (
      <Stack sx={{ height: "100%" }} justifyContent="center" alignItems="center">
        {imagesHidden ? (
          <ImageNotSupported
            sx={{
              width: 64,
              height: 64
            }}
            color="disabled"
          />
        ) : (
          <Typography>No image</Typography>
        )}
      </Stack>
    );
  }

  return (
    <Box sx={{ position: "relative", width }}>
      <LightboxModal
        isOpen={open}
        images={images}
        photoIndex={modalIndex}
        cropTopImage={cropTopImage}
        onClose={closeModal}
        setPhotoIndex={setModalIndex}
      />
      <CarouselArrows
        filled
        size={arrowIconSize}
        shape="rounded"
        length={filteredPictures.length}
        onNext={handleNext}
        onPrevious={handlePrev}
      >
        <Box
          sx={{
            "& > .slick-slider > .slick-list": {
              opacity: imagesHidden ? 0.5 : 1,
              transition: "opacity 300ms"
            }
          }}
        >
          <Carousel ref={carouselRef} {...carouselSettings}>
            {filteredPictures.map(({ id, src, alt }, index) => {
              const isImageHidden = hiddenPictures?.includes(src);

              return (
                <Image
                  sx={{
                    height: height ?? 208,
                    ...(isImageHidden && blurredImageStyles)
                  }}
                  src={src}
                  alt={alt}
                  onClick={listingMatch ? preventBubbling(handleOpenModal(index)) : undefined}
                  cropTopImage={cropTopImage}
                  key={id}
                />
              );
            })}
          </Carousel>
        </Box>
      </CarouselArrows>
    </Box>
  );
}

type BlurredImageStyles = Pick<CSSProperties, "filter" | "opacity" | "transition">;

export const blurredImageStyles: BlurredImageStyles = {
  filter: "blur(0.1rem)",
  opacity: 0.5,
  transition: "all 200ms"
};

export function getFilteredPictures(
  pictures: CarouselCardProps["pictures"],
  filterHiddenPictures: boolean,
  hiddenPictures: string[]
): CarouselCardProps["pictures"] {
  if (!pictures) return [];

  const shouldFilterPictures = filterHiddenPictures && hiddenPictures?.length;

  if (!shouldFilterPictures) return pictures;

  return pictures.filter(({ src }) => !hiddenPictures.includes(src));
}
